import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

const DEFAULT_ALLOWED_ROLES = ["doctor", "patient", "admin"];

const RequireAuth = ({
  children,
  allowedRoles = DEFAULT_ALLOWED_ROLES,
  requireDoctorApproval = true
}) => {
  const [user, loading] = useAuthState(auth);
  const [role, setRole] = useState(null);
  const [doctorStatus, setDoctorStatus] = useState(null);
  const [checkingRole, setCheckingRole] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let active = true;
    const checkUserRole = async () => {
      if (!user || !user.emailVerified) {
        if (active) {
          setRole(null);
          setDoctorStatus(null);
          setCheckingRole(false);
        }
        return;
      }

      setCheckingRole(true);
      try {
        // 1. Check Session Cache first
        const cachedRole = sessionStorage.getItem(`role_${user.uid}`);
        const cachedStatus = sessionStorage.getItem(`status_${user.uid}`);

        // If we have a cached role, use it immediately to skip network delay
        // CRITICAL FIX: Only trust cache if it is 'approved'. If it says 'pending', ignore it and fetch fresh data.
        const isCacheValid = cachedRole && (cachedRole !== 'doctor' || cachedStatus === 'approved');

        if (isCacheValid) {
          setRole(cachedRole);
          setDoctorStatus(cachedStatus);
          setCheckingRole(false);
          // We can optionally re-verify in background if needed, but for speed, trust cache for session
          return;
        }

        // 2. Parallel Fetch: Query all collections simultaneously
        const [adminSnap, doctorSnap, patientSnap] = await Promise.all([
          getDoc(doc(db, "admins", user.uid)),
          getDoc(doc(db, "doctors", user.uid)),
          getDoc(doc(db, "patients_auth", user.uid))
        ]);

        if (!active) return;

        let foundRole = null;
        let foundStatus = null;

        // 3. Determine Role Priority
        if (adminSnap.exists()) {
          foundRole = "admin";
          foundStatus = "approved";
        } else if (doctorSnap.exists()) {
          const data = doctorSnap.data() || {};
          if (data.role === "admin") {
            foundRole = "admin";
            foundStatus = "approved";
          } else {
            foundRole = "doctor";
            foundStatus = data.verificationStatus || "pending";
          }
          if (foundRole === "doctor" && data.institution === "bmu") {
            sessionStorage.setItem(`institution_${user.uid}`, "bmu");
            sessionStorage.setItem(`bmuRole_${user.uid}`, data.bmuRole || "resident");
          }
        } else if (patientSnap.exists()) {
          foundRole = "patient";
          foundStatus = null;
        }

        // 4. Update State and Cache
        setRole(foundRole);
        setDoctorStatus(foundStatus);

        // 4. Update State and Cache
        setRole(foundRole);
        setDoctorStatus(foundStatus);

        if (foundRole) {
          // Only cache if the status is APPROVED (or if it's a role without status like patient)
          // This prevents "pending" users from getting stuck in a cached pending state after approval
          if (foundRole !== "doctor" || foundStatus === "approved") {
            sessionStorage.setItem(`role_${user.uid}`, foundRole);
            if (foundStatus) {
              sessionStorage.setItem(`status_${user.uid}`, foundStatus);
            }
          } else {
            // If pending/rejected, ensure we don't have stale cache (clean it up)
            sessionStorage.removeItem(`role_${user.uid}`);
            sessionStorage.removeItem(`status_${user.uid}`);
          }
        }

      } catch (err) {
        console.error("Role check failed:", err);
        if (active) {
          setRole(null);
          setDoctorStatus(null);
        }
      } finally {
        if (active) {
          setCheckingRole(false);
        }
      }
    };

    checkUserRole();
    return () => {
      active = false;
    };
  }, [user]);

  const needsVerification = user && !user.emailVerified;
  const isOnVerifyPage = location.pathname === "/verify-email";
  const isOnPendingPage = location.pathname === "/doctor/pending-approval";
  const isBMU = user ? sessionStorage.getItem(`institution_${user.uid}`) === "bmu" : false;

  if (loading || checkingRole) {
    return <div className="p-10">Checking access...</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsVerification && !isOnVerifyPage) {
    return (
      <Navigate
        to="/verify-email"
        state={{ email: user.email, from: location }}
        replace
      />
    );
  }

  if (needsVerification && isOnVerifyPage) {
    return children;
  }

  if (role && !allowedRoles.includes(role)) {
    if (role === "patient") {
      return <Navigate to="/patient/portal" replace />;
    }
    if (role === "doctor") {
      return <Navigate to="/dashboard" replace />;
    }
    if (role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/" replace />;
  }

  // BMU: redirect main dashboard to BMU dashboard for BMU doctors
  if (role === "doctor" && isBMU && location.pathname === "/dashboard") {
    return <Navigate to="/bmu/dashboard" replace />;
  }
  // BMU: only BMU doctors and admins can access /bmu/*
  if (location.pathname.startsWith("/bmu") && !isBMU && role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    role === "doctor" &&
    doctorStatus !== "approved" &&
    requireDoctorApproval &&
    !isOnPendingPage
  ) {
    return <Navigate to="/doctor/pending-approval" replace />;
  }

  if (role === "doctor" && doctorStatus !== "approved" && isOnPendingPage) {
    return children;
  }

  // CRITICAL FIX: If doctor IS approved but stuck on pending page, send them to dashboard
  if (role === "doctor" && doctorStatus === "approved" && isOnPendingPage) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default RequireAuth;
